import { Card, Table, Button } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ZorbaSelect } from '../../../../components/forms';
import { postRequest } from '../../../../services/requestService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../services/notificationsService';
import { isMobile } from '../../../../services/clientService';
import { getAllAgents } from '../../../../reducer/User/actions';
import { ADMIN_USERS, getAccessibleAgents, getTableColumns } from './constants';

import './index.scss';

export const AgentContacts = () => {
  const { userDetails } = useSelector(({ AuthReducer }) => AuthReducer);
  const { agents } = useSelector(({ UserReducer }) => UserReducer);
  const dispatch = useDispatch();
  const options = getAccessibleAgents(userDetails.email, userDetails._id, agents, userDetails);
  const [selectValue, setSelectValue] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [tempValue, setTempValue] = useState(null);
  const isAdmin = ADMIN_USERS.includes(userDetails.email);
  const tableScroll = {
    scrollToFirstRowOnChange: true,
    x: isMobile() ? true : 'max-content',
  };

  const pagination = {
    position: ['bottomRight'],
    showTotal: (totalItems, range) =>
      `${range[0]}-${range[1]} of ${totalItems} items`,
    defaultPageSize: 50,
  };

  useEffect(() => {
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        await dispatch(getAllAgents());
      } catch (error) {
        showErrorToast('Failed to load agents!');
      } finally {
        setAgentsLoading(false);
      }
    };
    loadAgents();
  }, [dispatch]);

  useEffect(() => {
    if (options?.length > 0 && !selectValue) {
      setSelectValue(options[0].value);
    }
  }, [options, selectValue]);

  useEffect(() => {
    const fetchReport = async (agentId) => {
      if (agentId === null || agentsLoading) return; // Don't fetch if no agent selected or agents still loading
      
      setIsLoading(true);
      try {
        const response = await postRequest(
          '/backoffice/contacts-report/get-report',
          { agentId: agentId || null }, // Send null for "All Agents" case
        );
        setData(response?.data?.data || []);
      } catch (error) {
        showErrorToast('Fetching report failed!');
      }
      setIsLoading(false);
    };

    fetchReport(selectValue);
  }, [selectValue, agentsLoading]);

  const handleAssociatedAgentChange = async (
    index,
    userId,
    associatedAgentId,
  ) => {
    setIsLoading(true);
    try {
      setTempValue(associatedAgentId);
      const response = await postRequest(
        `/backoffice/contacts-report/set-associated-agent/${userId}`,
        { associatedAgentId },
      );
      const newData = [...data];

      newData[index].contactOwnerId = response.data.associatedAgentId;
      newData[index].contactOwner = response.data.associatedAgentName;
      setData(newData);
      setEditingKey(null);
      showSuccessToast('Agent successfully associated!');
    } catch (error) {
      showErrorToast('Setting associated agent failed!');
    }
    setIsLoading(false);
  };

  const handleClearAgent = () => {
    console.log('Clearing agent, current selectValue:', selectValue);
    setSelectValue('');
    setData([]);
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {data.length > 0 && (
            <span style={{
              background: '#f0f0f0',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              {data.length} contact{data.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      }
      className="agent-contacts"
    >
      <div className="agent-select-container">
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <ZorbaSelect
            options={options}
            value={selectValue}
            onChange={(e) => setSelectValue(e)}
            className="agent-select"
            placeholder={agentsLoading ? "Loading agents..." : "Select an agent"}
            disabled={agentsLoading}
          />
          {selectValue && selectValue !== '' && (
            <button
              onClick={handleClearAgent}
              style={{
                position: 'absolute',
                right: '24px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid #d9d9d9',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '12px',
                color: '#666',
                padding: '2px',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}
              title="Clear selection"
              onMouseEnter={(e) => {
                e.target.style.background = '#f5f5f5';
                e.target.style.color = '#333';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                e.target.style.color = '#666';
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>
      <Table
        columns={getTableColumns({
          isAdmin,
          editingKey,
          setEditingKey,
          handleAssociatedAgentChange,
          tempValue,
          agents,
        })}
        dataSource={data}
        loading={isLoading || agentsLoading}
        scroll={tableScroll}
        pagination={pagination}
      />
    </Card>
  );
};
